import React, { FC, useEffect, useMemo, useState } from 'react';

import useLocales from 'hooks/useLocales';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import IncomingRequests from 'components/shared/IncomingRequests/Index';
import OutgoingRequests from 'components/shared/OutgoingRequests/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import ConfirmPopup from 'components/shared/Popups/Confirm/Index';
import ProfilePopUp from 'components/shared/ProfilePopUp/Index';
import Navbar from 'components/Navbar';
import MatchResultHeader from 'components/matchResultHeader';
import { useParams } from 'react-router';
import Footer from 'components/Footer/Index';
import NoData from 'components/shared/NoData/Index';
import { MatchProfileService } from 'services/matchProfile/matchProfile';
import { RequestsService } from 'services/requests/request';
import { SaveMatchProfileOne } from '../../redux/slices/match';
import { CircularProgress, TextField } from '@mui/material';
import Pagination from '@material-ui/core/Pagination';
import { useDebounce } from 'helpers/useDebounce';
import Image from 'components/image/Image';
import OrderBySelect from 'components/shared/OrderBySelect/Index';
import {
  DEFAULT_ORDERING,
  ScoreOrdering
} from 'core/constants/ordering';
import { STAGE_TYPES, STATUS_TYPES } from 'core/constants/jobCardConstant';

import './Index.scss';

const MatchCandidate: FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { t } = useLocales();
  const { matchProfileOne } = useSelector((state: RootState) => state.match);
  const { singleRequest }: any = useSelector(
    (state: RootState) => state.requests
  );
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [activeStage, setActiveStage] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(6);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [ordering, setOrdering] = useState<ScoreOrdering>(DEFAULT_ORDERING);
  const [outgoingPage, setOutgoingPage] = useState(1);
  const [outgoingLimit] = useState(6);
  const [outgoingOffset, setOutgoingOffset] = useState(0);
  const [outgoingSearchQuery, setOutgoingSearchQuery] = useState<string>('');
  const debouncedOutgoingSearchQuery = useDebounce(outgoingSearchQuery, 500);
  const [outgoingOrdering, setOutgoingOrdering] =
    useState<ScoreOrdering>(DEFAULT_ORDERING);
  const [filteredOutgoing, setFilteredOutgoing] = useState<any[]>([]);
  const [lastFilterStatus, setLastFilterStatus] = useState<number | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<number, number>>({
    0: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0
  });
  const [profileLoading, setProfileLoading] = useState(true);

  const isProfileForCurrentRoute =
    matchProfileOne?.id != null && String(matchProfileOne.id) === id;
  const displayProfile =
    profileLoading || !isProfileForCurrentRoute ? null : matchProfileOne;

  const selectedCandidates = useMemo(
    () => matchProfileOne?.selected_candidates || [],
    [matchProfileOne]
  );

  const stageCounts = useMemo(() => {
    const counts: Record<number, number> = {
      0: 0,
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    };
    selectedCandidates.forEach((el: { stage: number }) => {
      if (el.stage in counts) {
        counts[el.stage] += 1;
      }
    });
    return counts;
  }, [selectedCandidates]);

  const filteredCandidates = useMemo(() => {
    let list = [...selectedCandidates];

    if (activeStage !== null) {
      list = list.filter((el: { stage: number }) => el.stage === activeStage);
    }

    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      list = list.filter((el: any) => {
        const name = el?.candidate?.name?.toLowerCase() || '';
        const email = el?.candidate?.email?.toLowerCase() || '';
        const pitch = el?.candidate?.pitch?.toLowerCase() || '';
        return (
          name.includes(query) || email.includes(query) || pitch.includes(query)
        );
      });
    }

    list.sort((a: any, b: any) => {
      if (ordering === 'culture_score') {
        return (b?.culture_score ?? 0) - (a?.culture_score ?? 0);
      }
      if (ordering === 'created_at') {
        return (
          new Date(b?.created_at || 0).getTime() -
          new Date(a?.created_at || 0).getTime()
        );
      }
      return (b?.score ?? 0) - (a?.score ?? 0);
    });

    return list;
  }, [selectedCandidates, activeStage, debouncedSearchQuery, ordering]);

  const paginatedCandidates = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredCandidates.slice(start, start + limit);
  }, [filteredCandidates, page, limit]);

  const handleModal = (open: boolean, requestId: number) => {
    setOpenModal(open);

    const fromSelected = selectedCandidates.find(
      (el: { id: number }) => el.id === requestId
    );
    const fromOutgoing = singleRequest?.results?.find(
      (el: { id: number }) => el.id === requestId
    );
    const element = fromSelected || fromOutgoing;

    if (!element) {
      return;
    }

    setSelectedRequest(element);
  };

  const handleClosePopup = () => {
    setOpenModal(false);
    setSelectedRequest(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      dispatch(SaveMatchProfileOne(null));
      setProfileLoading(true);
      try {
        await MatchProfileService.getMatchProfileById(+id);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setProfileLoading(false);
      }
    };
    fetchData();
  }, [dispatch, id]);

  useEffect(() => {
    setOutgoingOffset((outgoingPage - 1) * outgoingLimit);
  }, [outgoingPage, outgoingLimit]);

  useEffect(() => {
    if (!id) return;
    RequestsService.getRequest(
      +id,
      debouncedOutgoingSearchQuery,
      outgoingLimit,
      outgoingOffset,
      outgoingOrdering,
      'profileID'
    );
  }, [
    id,
    debouncedOutgoingSearchQuery,
    outgoingLimit,
    outgoingOffset,
    outgoingOrdering
  ]);

  useEffect(() => {
    const newStatusCounts: Record<number, number> = {
      0: 0,
      1: 0,
      2: 0,
      3: 0,
      4: 0
    };
    singleRequest?.results?.forEach((result: { status: number }) => {
      if (result?.status in newStatusCounts) {
        newStatusCounts[result.status] += 1;
      }
    });
    setStatusCounts(newStatusCounts);
    setLastFilterStatus(null);
    setFilteredOutgoing(singleRequest?.results || []);
  }, [singleRequest]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, id, activeStage]);

  useEffect(() => {
    setOutgoingPage(1);
  }, [debouncedOutgoingSearchQuery, id]);

  const handleOrderingChange = (value: ScoreOrdering): void => {
    setOrdering(value);
    setPage(1);
  };

  const handleOutgoingOrderingChange = (value: ScoreOrdering): void => {
    setOutgoingOrdering(value);
    setOutgoingPage(1);
    setOutgoingOffset(0);
  };

  const cancelHandler = (candidateId: number) => {
    setPendingDeleteId(candidateId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (pendingDeleteId !== null) {
      MatchProfileService.deleteCultureProfile(pendingDeleteId);
    }
    setDeleteConfirmOpen(false);
    setPendingDeleteId(null);
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPendingDeleteId(null);
  };

  const changeStage = (candidateId: number, data: { stage: number }) => {
    MatchProfileService.updateSelectedCandidate(candidateId, data);
  };

  const stageOutgoingHandler = (requestId: number, data: any) => {
    RequestsService.changeRequest(data, requestId, 'find-candidate');
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleOutgoingSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOutgoingSearchQuery(event.target.value);
  };

  const handleStageFilter = (stage: number) => {
    setActiveStage((prev) => (prev === stage ? null : stage));
  };

  const handleStatusFilter = (type: number) => {
    if (lastFilterStatus === type) {
      setFilteredOutgoing(singleRequest?.results || []);
      setLastFilterStatus(null);
      return;
    }
    setFilteredOutgoing(
      singleRequest?.results?.filter(
        (app: { status: number }) => app.status === type
      ) || []
    );
    setLastFilterStatus(type);
  };

  return (
    <div
      className="matchCandidate"
      style={{ display: 'flex', flexDirection: 'column', height: 'auto' }}
    >
      <Navbar />
      <MatchResultHeader data={displayProfile} />
      <div className="main" style={{ flex: 1 }}>
        <div className="candidateOver">
          <div className="candidateOver__right">
            <div className="candidateOver__right__top">
              <div className="candidateOver__right__top-header">
                <span className="candidateOver__right__top-title">
                  {t('Selected candidates')}
                </span>
                <OrderBySelect
                  value={ordering}
                  onChange={handleOrderingChange}
                />
              </div>
              <div className="candidateOver__right__top__counts">
                {STAGE_TYPES.map((stageType) => (
                  <button
                    key={stageType.value}
                    className={`candidateOver__right__top__counts-pass btn${
                      activeStage === stageType.value ? ' active' : ''
                    }`}
                    onClick={() => handleStageFilter(stageType.value)}
                  >
                    {activeStage === stageType.value && (
                      <Image name="check.svg" />
                    )}
                    {t(stageType.to_show)} ({stageCounts[stageType.value]})
                  </button>
                ))}
              </div>
              <div className="search-container">
                <TextField
                  value={searchQuery}
                  onChange={handleSearch}
                  className="dropdown-search"
                  placeholder="Search"
                  variant="outlined"
                />
              </div>
            </div>
            <div className="candidateOver__right__bottom">
              {profileLoading ||
              !isProfileForCurrentRoute ||
              matchProfileOne === null ? (
                <div className="loadingContainer">
                  <CircularProgress className="loading" />
                </div>
              ) : paginatedCandidates.length ? (
                paginatedCandidates.map((item: any) => (
                  <IncomingRequests
                    key={item.id}
                    id={item.id}
                    name={item.candidate?.name}
                    email={item.candidate?.email}
                    sub_title={item.candidate?.pitch}
                    icon={item.candidate?.avatar}
                    score={item.score}
                    culture_score={item.culture_score}
                    date={item.created_at}
                    industry={item.candidate?.industries}
                    stage={item.stage}
                    status={item.status}
                    selectedBy={item.selected_by}
                    candidateId={item.candidate?.id}
                    isSelectedCandidate
                    onStageChange={changeStage}
                    onDelete={cancelHandler}
                    showDelete
                    setOpen={() => handleModal(true, item.id)}
                  />
                ))
              ) : (
                <div className="no_data">
                  <NoData />
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16
                }}
              >
                <Pagination
                  className="pagination"
                  count={Math.ceil(filteredCandidates.length / limit) || 1}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </div>
            </div>
          </div>

          <div className="candidateOver__right">
            <div className="candidateOver__right__top">
              <div className="candidateOver__right__top-header">
                <span className="candidateOver__right__top-title">
                  {t('Outgoing requests')}
                </span>
                <OrderBySelect
                  value={outgoingOrdering}
                  onChange={handleOutgoingOrderingChange}
                />
              </div>
              <div className="candidateOver__right__top__counts">
                {STATUS_TYPES.map((statusType) => (
                  <button
                    onClick={() => handleStatusFilter(statusType.value)}
                    key={statusType.value}
                    className="candidateOver__right__top__counts-pass btn"
                  >
                    {lastFilterStatus === statusType.value && (
                      <Image name="check.svg" />
                    )}
                    {t(statusType.label)} ({statusCounts[statusType.value]})
                  </button>
                ))}
              </div>
              <div className="search-container">
                <TextField
                  value={outgoingSearchQuery}
                  onChange={handleOutgoingSearch}
                  className="dropdown-search"
                  placeholder="Search"
                  variant="outlined"
                />
              </div>
            </div>
            <div className="candidateOver__right__bottom">
              {singleRequest === null ? (
                <div className="loadingContainer">
                  <CircularProgress className="loading" />
                </div>
              ) : filteredOutgoing?.length ? (
                filteredOutgoing.map((item: any) => (
                  <OutgoingRequests
                    key={item.id}
                    id={item.id}
                    name={item.candidate?.name}
                    email={item.candidate?.email}
                    sub_title={item.candidate?.pitch}
                    icon={item.candidate?.avatar}
                    score={item.score}
                    culture_score={item.culture_score}
                    date={item.created_at}
                    industry={item.candidate?.industries}
                    stage={item.stage}
                    history={item.history}
                    status={item.status}
                    stageOutgoingHandler={stageOutgoingHandler}
                    setOpen={() => handleModal(true, item.id)}
                    jobTitle={
                      item?.origin_job?.title ||
                      item?.origin_profile?.title ||
                      ''
                    }
                  />
                ))
              ) : (
                <div className="no_data">
                  <NoData />
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16
                }}
              >
                <Pagination
                  className="pagination"
                  count={Math.ceil((singleRequest?.count || 0) / outgoingLimit)}
                  page={outgoingPage}
                  onChange={(_, value) => setOutgoingPage(value)}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </div>
            </div>
          </div>
        </div>
        {deleteConfirmOpen && (
          <Popup
            className="popUp"
            title={t('Delete Candidate')}
            onClose={cancelDelete}
          >
            <ConfirmPopup
              description={t('Are you sure you want to delete this candidate?')}
              onApprove={confirmDelete}
              onCancel={cancelDelete}
              approveTitle={t('Yes, Delete')}
              cancelTitle={t('Cancel')}
              t={t}
            />
          </Popup>
        )}
        {openModal && selectedRequest && (
          <Popup
            className="popUp"
            title={t('Match Profile')}
            description={displayProfile?.title}
            onClose={handleClosePopup}
          >
            <ProfilePopUp
              selectedRequest={selectedRequest}
              getJob={
                selectedRequest?.origin_job ||
                selectedRequest?.origin_profile ||
                displayProfile
              }
              id={selectedRequest?.id}
              onClick={() => {
                if (selectedRequest?.candidate && !selectedRequest?.origin_job) {
                  cancelHandler(selectedRequest?.id);
                } else {
                  stageOutgoingHandler(selectedRequest?.id, { status: 4 });
                }
              }}
              status={selectedRequest.status}
              isCandidate={false}
            />
          </Popup>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MatchCandidate;
